import ErrorHelper from "../logging/ErrorHelper";
import { IEventRecord, IEventRecordsByName, IEventRecordList, IDeclaredEventsByName } from './Events';
import * as Events from './Events';

export { ICustomEvent } from './Events';

function logToErrorHelper(error: Error): void {
    ErrorHelper.log(error);
}

/** An instance of EventGroup allows anything with a handle to it to trigger events on it.
 *  If the target is an HTMLElement, the event will be attached to the element and can be
 *  triggered as usual (like clicking for onclick).
 *  The event can be triggered by calling EventGroup.raise() here. If the target is an
 *  HTMLElement, the event gets raised and is handled by the browser. Otherwise, it gets
 *  handled here in EventGroup, and the handler is called in the context of the parent
 *  (which is passed in in the constructor).
 */
export class EventGroup {
    private static _uniqueId = 0;
    private _parent;
    private _eventRecords: IEventRecord[];
    private _id = EventGroup._uniqueId++;
    private _isDisposed: boolean;
    private _onError: (error: Error) => void;

    /** parent: the context in which events attached to non-HTMLElements are called */
    public constructor(parent: any, onError: (error: Error) => void = logToErrorHelper) {
        this._parent = parent;
        this._onError = onError;
        this._eventRecords = [];
    }

    /**
     *  Events raised here by default have bubbling set to false and cancelable set to true.
     *  This applies also to built-in events being raised manually here on HTMLElements,
     *  which may lead to unexpected behavior if it differs from the defaults.
     */
    public static raise<T = {}>(
        target: any,
        eventName: string,
        eventArgs?: T,
        bubbleEvent?: boolean
    ): boolean | undefined {
        return Events.raise(target, eventName, eventArgs, bubbleEvent);
    }

    public static isObserved(target: any, eventName: string): boolean {
        let events = target && <IEventRecordsByName>target.__events__;

        return !!events && !!events[eventName];
    }

    /** Check to see if the target has declared support of the given event. */
    public static isDeclared(target: any, eventName: string): boolean {
        let declaredEvents = target && <IDeclaredEventsByName>target.__declaredEvents;

        return !!declaredEvents && !!declaredEvents[eventName];
    }

    public static stopPropagation(event: any) {
        if (event.stopPropagation) {
            event.stopPropagation();
        }
    }

    public dispose() {
        if (!this._isDisposed) {
            this._isDisposed = true;

            this.off();
            this._parent = null;
        }
    }

    /** On the target, attach a set of events, where the events object is a name to function mapping. */
    public onAll(target: any, events: { [key: string]: (args?: any) => void; }, useCapture?: boolean) {
        for (var eventName in events) {
            this.on(target, eventName, events[eventName], useCapture);
        }
    }

    /** On the target, attach an event whose handler will be called in the context of the parent
     * of this instance of EventGroup.
     */
    public on<T = {}>(target: any, eventName: string, callback: (args?: T) => void, useCapture?: boolean) {
        if (eventName.indexOf(',') > -1) {
            var events = eventName.split(/[ ,]+/);

            for (var i = 0; i < events.length; i++) {
                this.on(target, events[i], callback, useCapture);
            }
        } else {
            var parent = this._parent;
            var eventRecord: IEventRecord = {
                target: target,
                eventName: eventName,
                parent: parent,
                callback: callback,
                objectCallback: null,
                elementCallback: null,
                useCapture: useCapture
            };

            // Initialize and wire up the record on the target, so that it can call the callback if the event fires.
            let events = <IEventRecordsByName>(target.__events__ = target.__events__ || {});
            events[eventName] = events[eventName] || <IEventRecordList>{
                count: 0
            };
            events[eventName][this._id] = events[eventName][this._id] || [];
            (<IEventRecord[]>events[eventName][this._id]).push(eventRecord);
            events[eventName].count++;

            if (Events.isDOMEventTarget(target)) {
                let processElementEvent = (...args: any[]) => {
                    if (this._isDisposed) {
                        return;
                    }

                    try {
                        var result = callback.apply(parent, args);
                        if (result === false && args[0] && args[0].preventDefault) {
                            var e = args[0];

                            e.preventDefault();
                            EventGroup.stopPropagation(e);
                        }
                    } catch (e) {
                        this._onError(e);
                    }

                    return result;
                };

                eventRecord.elementCallback = processElementEvent;

                /* tslint:disable:ban-native-functions */
                target.addEventListener(eventName, processElementEvent, useCapture);
                /* tslint:enable:ban-native-functions */
            } else {
                let processObjectEvent = (...args: any[]) => {
                    if (this._isDisposed) {
                        return;
                    }

                    return callback.apply(parent, args);
                };

                eventRecord.objectCallback = processObjectEvent;
            }

            // Remember the record locally, so that it can be removed.
            this._eventRecords.push(eventRecord);
        }
    }

    public off<T = {}>(target?: any, eventName?: string, callback?: (args?: T) => void, useCapture?: boolean) {
        for (var i = 0; i < this._eventRecords.length; i++) {
            var eventRecord = this._eventRecords[i];
            if ((!target || target === eventRecord.target) &&
                (!eventName || eventName === eventRecord.eventName) &&
                (!callback || callback === eventRecord.callback) &&
                ((typeof useCapture !== 'boolean') || useCapture === eventRecord.useCapture)) {
                let events = <IEventRecordsByName>eventRecord.target.__events__;
                var targetArrayLookup = events[eventRecord.eventName];
                var targetArray = targetArrayLookup ? <IEventRecord[]>targetArrayLookup[this._id] : null;

                // We may have already target's entries, so check for null.
                if (targetArray) {
                    if (targetArray.length === 1 || !callback) {
                        targetArrayLookup.count -= targetArray.length;
                        delete events[eventRecord.eventName][this._id];
                    } else {
                        targetArrayLookup.count--;
                        targetArray.splice(targetArray.indexOf(eventRecord), 1);
                    }

                    if (!targetArrayLookup.count) {
                        delete events[eventRecord.eventName];
                    }
                }

                if (eventRecord.elementCallback) {
                    if (eventRecord.target.removeEventListener) {
                        eventRecord.target.removeEventListener(eventRecord.eventName, eventRecord.elementCallback, eventRecord.useCapture);
                    }
                }

                this._eventRecords.splice(i--, 1);
            }
        }
    }

    /** Trigger the given event in the context of this instance of EventGroup. */
    public raise<T = {}>(eventName: string, eventArgs?: T, bubbleEvent?: boolean): any {
        return EventGroup.raise(this._parent, eventName, eventArgs, bubbleEvent);
    }

    /** Declare an event as being supported by this instance of EventGroup. */
    public declare(event: any) {
        var declaredEvents = this._parent.__declaredEvents = this._parent.__declaredEvents || {};

        if (typeof event === 'string') {
            declaredEvents[event] = true;
        } else {
            for (var i = 0; i < event.length; i++) {
                declaredEvents[event[i]] = true;
            }
        }
    }
}

export default EventGroup;
